import {
  arrow,
  autoUpdate,
  computePosition,
  flip,
  offset,
  shift,
} from "@floating-ui/dom";
import * as Sentry from "@sentry/browser";
import { setDateRangePickerChanged } from "../state";
import { SIGNIN_PAGES } from "./constants";

export const urlParams = new URLSearchParams(window.location.search);

const LOCALE = new Intl.NumberFormat().resolvedOptions().locale;

const CACHE = {};

export const chartColors = [
  "#049BD7",
  "#e60049",
  "#50e991",
  "#e6d800",
  "#9b19f5",
  "#ffa300",
  "#dc0ab4",
  "#b3d4ff",
  "#00bfa0",
];

export const LOGO_MAPPING = {
  "angel.co": "fab fa-angellist",
  "artstation.com": "fab fa-artstation",
  "behance.net": "fab fa-behance-square",
  "blogger.com": "fab fa-blogger",
  "dev.to": "fab fa-dev",
  "deviantart.com": "fab fa-deviantart",
  "dribbble.com": "fab fa-dribbble",
  "facebook.com": "fab fa-facebook-f",
  "flickr.com": "fab fa-flickr",
  "foursquare.com": "fab fa-foursquare",
  "github.com": "fab fa-github-alt",
  "instagram.com": "fab fa-instagram",
  "kaggle.com": "fab fa-kaggle",
  "linkedin.com/in/": "fab fa-linkedin-in",
  "linkedin.com/sales/": "fab fa-linkedin",
  "linkedin.com/recruiter/": "fab fa-linkedin",
  "linkedin.com/talent/": "fab fa-linkedin",
  "medium.com": "fab fa-medium",
  "meetup.com": "fab fa-meetup",
  "pinterest.com": "fab fa-pinterest-p",
  "quora.com": "fab fa-quora",
  "reddit.com": "fab fa-reddit-alien",
  "researchgate.net": "fab fa-researchgate",
  "soundcloud.com": "fab fa-soundcloud",
  "stackexchange.com": "fab fa-stack-exchange",
  "stackoverflow.com": "fab fa-stack-overflow",
  "tumblr.com": "fab fa-tumblr",
  "twitter.com": "fab fa-twitter",
  "vk.com": "fab fa-vk",
  "vimeo.com": "fab fa-vimeo-v",
  "wordpress.com": "fab fa-wordpress-simple",
  "xing.com": "fab fa-xing",
  "youtube.com": "fab fa-youtube",
};

export const CRMS = {
  airtable: "Airtable",
  applicantstack: "ApplicantStack",
  ashby: "Ashby",
  asymbl: "Asymbl",
  avionte: "Avionté",
  bigbiller: "Top Echelon",
  bluesky: "BlueSky",
  bullhorn: "Bullhorn",
  breezy: "Breezy HR",
  cats: "CATS",
  chameleoni: "Chameleon-i",
  clickup: "ClickUp",
  clockwork: "Clockwork Recruiting",
  colleague: "Colleague",
  comeet: "Comeet",
  copper: "Copper",
  crelate: "Crelate",
  dynamics: "Dynamics",
  eboss: "eBoss",
  encore: "Encore",
  exelare: "Exelare",
  ezekia: "Ezekia",
  firefish: "Firefish",
  googlecontacts: "Google Contacts",
  greenhouse: "Greenhouse",
  highlevel: "GoHighLevel",
  hiregenius: "ShadowOne",
  homerun: "Homerun",
  hubspot: "HubSpot",
  invenias: "Invenias",
  itris: "itris",
  jazz: "JazzHR",
  jobadder: "JobAdder",
  jobdiva: "JobDiva",
  jobvite: "Jobvite",
  kortivity: "Kortivity",
  lessannoying: "Less Annoying CRM",
  lever: "Lever",
  loxo: "Loxo",
  manatal: "Manatal",
  mercury: "Mercury xRM",
  pcrecruiter: "PCRecruiter",
  peopleforce: "PeopleForce",
  personio: "Personio",
  pinpoint: "Pinpoint",
  pipedrive: "Pipedrive",
  podio: "Podio",
  pump: "Pump",
  recruitcrm: "Recruit CRM",
  recruitee: "Recruitee",
  recruiterflow: "Recruiterflow",
  recruitly: "Recruitly",
  recruitmentboost: "Recruitment Boost",
  s3: "S3",
  salesforce: "Salesforce",
  seven20: "Seven20",
  smartrecruiters: "SmartRecruiters",
  successfactors: "SuccessFactors",
  streak: "Streak",
  talentrover: "Bullhorn for Salesforce",
  taleo: "Taleo",
  targetrecruit: "TargetRecruit",
  teamtailor: "Teamtailor",
  thrive: "Thrive",
  tracker: "Tracker",
  vincere: "Vincere",
  voyager: "Voyager Infinity",
  workable: "Workable",
  zohocrm: "Zoho CRM",
  "zoho-recruit": "Zoho Recruit",
};

export const LOGO_MAPPING_COLORS = {
  "linkedin.com/sales": "gold",
  "linkedin.com/recruiter": "gold",
  "linkedin.com/talent": "gold",
};

export const uuidV4 = () =>
  "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;

    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });

export const titleCase = (string) => {
  const formattedString = string.charAt(0).toUpperCase() + string.slice(1);
  return formattedString.replace(/([A-Z])/g, " $1").trim();
};

export const nameAndPhoto = (name, photo, classList = "", link = null) => {
  let text = `<span class='d-inline-flex align-items-center'>${
    name || ""
  }</span>`;

  if (link) text = `<a href=${link} target="_blank">${text}</a>`;

  return `
      <div class="name-and-photo candidate-profile-link d-flex align-items-center my-1 user-select-none ${classList}">
        <img src=${
          photo || "/assets/img/icons/user.png"
        } class="rounded-circle photo-image mr-2" style="width: 1.5rem; height: 1.5rem">
        ${text}
      </div>`;
};

export const wrapTooltip = (
  content,
  tooltip,
  position = "bottom",
  wrapperClasses = "",
) => {
  const wrapperId = `tooltip-wrapper-${uuidV4()}`;
  const tooltipId = `tooltip-${uuidV4()}`;
  const arrowId = `${tooltipId}-arrow`;

  const htmlContent = `
    <div id="${wrapperId}" class="sw-tt-wrap ${wrapperClasses}">
      ${content}
      <div id="${tooltipId}" class="sw-tt-text d-none" role="tooltip">
        ${tooltip}
        <div id="${arrowId}" class="sw-tt-arrow"></div>
      </div>
    </div>
  `;

  const setupTooltip = () => {
    const wrapper = document.getElementById(wrapperId);
    const tooltipElement = document.getElementById(tooltipId);
    const arrowElement = document.getElementById(arrowId);

    if (!wrapper || !tooltipElement || !arrowElement) return;

    let cleanup;

    const update = () => {
      computePosition(wrapper, tooltipElement, {
        placement: position,
        middleware: [
          offset(8),
          flip(),
          shift({ padding: 5 }),
          arrow({ element: arrowElement }),
        ],
      }).then(({ x, y, placement, middlewareData }) => {
        Object.assign(tooltipElement.style, {
          left: `${x}px`,
          top: `${y}px`,
        });

        const { x: arrowX, y: arrowY } = middlewareData.arrow;

        const staticSide = {
          top: "bottom",
          right: "left",
          bottom: "top",
          left: "right",
        }[placement.split("-")[0]];

        Object.assign(arrowElement.style, {
          left: arrowX == null ? "" : `${arrowX}px`,
          top: arrowY == null ? "" : `${arrowY}px`,
          right: "",
          bottom: "",
          [staticSide]: "-4px",
        });

        tooltipElement.classList.remove("d-none");
        tooltipElement.classList.add("d-block");
      });
    };

    $(wrapper).on("mouseenter", () => {
      update();
      tooltipElement.style.opacity = "1";
      tooltipElement.style.visibility = "visible";
      cleanup = autoUpdate(wrapper, tooltipElement, update);
    });

    $(wrapper).on("mouseleave", () => {
      tooltipElement.style.opacity = "0";
      tooltipElement.style.visibility = "hidden";
      tooltipElement.classList.remove("d-block");
      tooltipElement.classList.add("d-none");

      if (cleanup) {
        cleanup();
        cleanup = null;
      }
    });
  };

  requestAnimationFrame(setupTooltip);

  return htmlContent;
};

export const photoAndTooltip = (user, size = "1.25rem") => {
  if (!user) return "";
  const email = user.email || "";
  return wrapTooltip(
    `<img
        src=${user.picture || user.photo || "/assets/img/icons/user.png"}
        class="rounded-circle"
        style="height: ${size}; width: ${size};""
        >`,
    `${user.name || "Unknown User"}<br>${email}`,
  );
};

export const yesNoConfirm = (
  message,
  yesCallback,
  noCallback = null,
  yesLabel = "Yes",
  noLabel = "No",
) =>
  bootbox.confirm({
    message,
    buttons: {
      cancel: {
        label: noLabel,
        className: "btn-light",
      },
      confirm: {
        label: yesLabel,
        className: "btn-primary ml-2",
      },
    },
    callback: (result) => {
      if (result) yesCallback();
      else if (noCallback) noCallback();
    },
  });

export const backdropAlert = (message, size = null) =>
  bootbox.alert({ message, size });

export const messageAlert = (message, title = null) =>
  bootbox.dialog({ title, message });

export const yesNoConfirmExtended = ({
  msgData,
  yesCallback,
  noCallback = null,
}) => {
  const { action, selectedCount, targetType, type, providerTitle } = msgData;

  if (selectedCount === 0) {
    backdropAlert("Please make a selection");
    return;
  }

  if (selectedCount === 1 && action === "Delete") {
    if (type === "campaign") {
      yesNoConfirm(
        `Delete 1 campaign? This campaign cannot be retrieved after deletion. Contacts will not be deleted but outreach will stop.`,
        () => yesCallback(),
        null,
        "Delete",
        "Cancel",
      );
    } else {
      yesNoConfirm(
        `Are you sure you would like to delete this ${targetType}?`,
        () => {
          yesCallback();
        },
      );
    }
    return;
  }

  if (selectedCount === 1) {
    yesCallback();
    return;
  }

  let title;
  let message;
  let className = "";

  if (type === "campaign") {
    title = "Delete Campaigns";
    message = `<p>You are going to delete <strong>${selectedCount}</strong> campaigns, these campaigns cannot be retrieved after deletion.</p><p style="font-weight:bold;">To continue with this action, please type below the number of campaigns to delete.</p>`;
  }

  if (type === "send") {
    title = "Send emails";
    message = `<p>You are going to ${action} emails to <strong>${selectedCount}</strong> contacts.</p><p style="font-weight:bold;">To continue with this action, please type below the number of contacts that will be changed.</p>`;
  }

  if (type === "inmail") {
    title = "Converting to InMail";
    message = `<p>You are going to convert <strong>${selectedCount}</strong> contacts to InMail.</p><p style="font-weight:bold;">To continue with this action, please type below the number of contacts to convert.</p>`;
  }

  const isDeleted = action === "deleted";

  if (type === "status") {
    title = `${isDeleted ? `Delete tasks` : `Change status`}`;
    message = `${
      isDeleted
        ? `<p>You are going to delete <strong>${selectedCount}</strong> tasks.</p><p style="font-weight:bold;">To continue with this action, please type below the number of contacts that will be changed.</p>`
        : `<p>You are going to change the status of <strong>${selectedCount}</strong> tasks to ${action}.</p><p style="font-weight:bold;">To continue with this action, please type below the number of contacts that will be changed.</p>`
    } `;
  }

  if (type === "stage") {
    title = "Change stage";
    message = `<p>You are going to change <strong>${selectedCount}</strong> contacts' stage to ${action}. </p><p style="font-weight:bold;">To continue with this action, please type below the number of contacts that will be changed.</p>`;
  }

  if (type === "enrich") {
    let msg;
    if (["email", "both"].includes(action)) {
      msg = `<p>You are going to enrich the emails of <strong>${selectedCount}</strong> contacts</p>`;
    } else if (action === "phone") {
      msg = `<p>You are going to enrich the phones of <strong>${selectedCount}</strong> contacts</p>`;
    } else if (providerTitle) {
      msg = `<p>You are going to enrich <strong>${selectedCount}</strong> contacts using ${providerTitle}</p>`;
    } else {
      msg = `<p>You are going to enrich the ${action} emails of <strong>${selectedCount}</strong> contacts</p>`;
    }

    title = "Enrich contacts";
    message = `${msg} <p style="font-weight:bold;">To continue with this action, please type below the number of contacts that will be enriched.</p>`;
    className = "enrichModal";
  }

  if (type === "modify") {
    title = `${action} ${targetType}s`;
    message = `<p>You are going to ${action.toLowerCase()} <strong>${selectedCount}</strong> ${targetType}s. </p><p style="font-weight:bold;">To continue with this action, please type below the number of ${targetType}s to ${action.toLowerCase()}.</p>`;
  }

  bootbox
    .prompt({
      title,
      message,
      className,
      inputType: "text",
      pattern: selectedCount,
      placeholder: selectedCount,
      required: true,
      buttons: {
        cancel: { label: "Cancel", className: "btn-light" },
        confirm: { label: "Confirm", className: "btn-primary ml-2" },
      },
      callback: (result) => {
        if (result) yesCallback();
        else if (noCallback) noCallback();
      },
    })
    .on("shown.bs.modal", (e) => {
      const $target = $(e.currentTarget);

      const input = $target.find(".bootbox-input-text");
      const confirmBtn = $target.find(".bootbox-accept");
      const closeBtn = $target.find(".bootbox-close-button");

      const modalHeader = $target.find(".modal-header");
      const modalTitle = $target.find(".modal-title");

      const bgColor = $target.hasClass("enrichModal") ? "#1da2d8" : "#e74a3b";

      modalHeader.css("background", bgColor);
      confirmBtn.css("background", bgColor);

      modalTitle.addClass("important-color-white");
      closeBtn.addClass("important-color-white");
      confirmBtn.addClass("important-color-white disabled");

      input.after(
        '<p id="error-message" style="display:none;margin-top:10px;margin-bottom:0;color:#e74a3b">You have entered an incorrect number of contacts.</p>',
      );

      $(input).on("input", (ev) => {
        const match = ev.target.value === selectedCount.toString();
        const error = ev.target.value.length > 0 && !match;
        confirmBtn.toggleClass("disabled", !match);
        $("#error-message").toggle(error);
        input.css("border", error ? "2px solid #e74a3b" : "1px solid #e4e6ec");
      });
    });
};

export const caseInsensitiveSort = (arr, key = null) => {
  const newArr = Array.from(arr);
  return key
    ? newArr.sort((a, b) =>
        a[key].toLowerCase().localeCompare(b[key].toLowerCase()),
      )
    : newArr.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
};

export const showToast = (opts = {}) => {
  const uniqueId = opts.id || uuidV4(); // Manually set an ID in opts if you wish to prevent a specific toast from stacking
  const toastId = `toast-${uniqueId}`;

  const toastExists = document.getElementById(toastId);

  if (toastExists) {
    return;
  }

  const {
    title, // required
    subtitle = "",
    type = "info", // info, success, warning, danger
    timeout = 4000,
    links = {},
  } = opts;

  const toastHtml = /* html */ `
        <div id="${toastId}" class="toast toast-${type}" style="display: none;">
          <div class="toast-icon-container"></div>
          <div class="toast-text-container">
            <span class="toast-text-container-title">${title}</span>
            <span class="toast-text-container-subtitle">${subtitle}</span>
          </div>
          <div class="toast-close">
            <i class="fa-regular fa-xmark"></i>
          </div>
        </div>`;

  const toastNotifications = document.getElementById("toast-notifications");

  toastNotifications.insertAdjacentHTML("beforeend", toastHtml);

  Object.entries(links).forEach(([kind, link]) => {
    const subtitleElement = document.querySelector(
      `#${toastId} .toast-text-container-subtitle`,
    );
    subtitleElement.insertAdjacentHTML(
      "beforeend",
      `<br /><a href="${link}" target="_blank">${kind}</a>`,
    );
  });

  const toast = document.getElementById(toastId);

  setTimeout(() => {
    toast.style.display = "flex";
    toast.style.opacity = "0";
    toast.style.transition = "opacity 400ms";
    toast.offsetHeight; // forces reflow
    toast.style.opacity = "1";
  }, 0);

  let toastTimeout;

  const removeToast = () => {
    // Fade out and remove the toast
    toast.style.opacity = "0";

    setTimeout(() => {
      toast.remove();
      // If no toasts left, clear the container
      if (toastNotifications.children.length === 0) {
        toastNotifications.innerHTML = "";
      }
    }, 400);
  };

  const startTimeout = () => {
    toastTimeout = setTimeout(() => removeToast(), timeout);
  };

  startTimeout();

  const closeButton = toast.querySelector(".toast-close");
  closeButton.addEventListener("click", () => removeToast());

  toastNotifications.addEventListener("mouseenter", () => {
    clearTimeout(toastTimeout);
  });

  toastNotifications.addEventListener("mouseleave", () => {
    startTimeout();
  });
};

// hide alerts instead of remove, when dismissed

export const showSlowProviderResponseMessage = () => {
  const mapping = {
    azure: "Outlook",
    exchange: "Exchange",
    gcp: "Gmail",
  };
  const provider = localStorage.getItem("provider");
  const providerTitle = mapping[provider] || "Your email provider";
  showToast({
    title: `${providerTitle} is taking a long time`,
    subtitle: "Please try again later or contact support if the issue persists",
    type: "warning",
  });
};

export const isValidEmail = (email) =>
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(
    email,
  );

export const saveUser = (user) => {
  const copy = { ...user };
  delete copy.signature;
  localStorage.setItem("user", JSON.stringify(copy));
};

export const getUser = () => {
  const user = localStorage.getItem("user");
  return user ? JSON.parse(user) : {};
};

export const getTimezone = () => {
  CACHE.tz =
    CACHE.tz ||
    getUser().timezone ||
    Intl.DateTimeFormat().resolvedOptions().timeZone;
  return CACHE.tz;
};

export const localeString = (d, opts) => {
  try {
    const cacheKey = `dateTimeFormatter:${JSON.stringify(opts)}`;
    CACHE[cacheKey] = CACHE[cacheKey] || new Intl.DateTimeFormat(LOCALE, opts);
    return CACHE[cacheKey].format(d);
  } catch {
    return "Unknown Date";
  }
};

export const localDateTimeString = (
  timestamp,
  weekday = true,
  timezone = null,
) => {
  const d = new Date(timestamp * 1000);
  const opts = {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: timezone || getTimezone(),
  };
  if (weekday) opts.weekday = "short";
  if (d.getFullYear() !== new Date().getFullYear()) {
    opts.year = "numeric";
  }
  return localeString(d, opts);
};

export const relativeLocalDateTimeString = (
  timestamp,
  weekday = true,
  timezone = null,
) => {
  const d = new Date(timestamp * 1000);
  const opts = {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: timezone || getTimezone(),
  };

  const isSameDay = (date1, date2) =>
    date1.getDate() === date2.getDate() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getFullYear() === date2.getFullYear();

  const today = new Date();
  if (isSameDay(d, today)) return `Today, ${localeString(d, opts)}`;

  const yesterday = new Date(today.setDate(today.getDate() - 1));
  return isSameDay(d, yesterday)
    ? `Yesterday, ${localeString(d, opts)}`
    : localDateTimeString(timestamp, weekday, timezone);
};

export const localDateString = (timestamp, weekday = true, year = true) => {
  const d = new Date(timestamp * 1000);
  const opts = {
    month: "short",
    day: "numeric",
    timeZone: getTimezone(),
  };
  if (weekday) opts.weekday = "short";
  if (year) opts.year = "numeric";
  return localeString(d, opts);
};

export const humanFriendlyTime = (timestamp) => {
  const delta = Math.round(Date.now() / 1000 - timestamp);

  const minute = 60;
  const hour = minute * 60;

  if (!timestamp) return "";

  if (delta < minute) return `${delta}s ago`;

  // eslint-disable-next-line sourcewhale/prefer-ternary
  if (delta < hour) return `${Math.floor(delta / minute)}min ago`;

  return localDateTimeString(timestamp, false);
};

const localTimeComponent = (timestamp, key) => {
  const d = new Date(timestamp * 1000);
  const opts = { timeZone: getTimezone(), hour12: false };
  opts[key] = key === "year" ? "numeric" : "2-digit";
  const ret = localeString(d, opts);
  return ret.length === 1 ? "0".concat(ret) : ret;
};

export const numericDateString = (timestamp) => {
  const year = localTimeComponent(timestamp, "year");
  const month = localTimeComponent(timestamp, "month");
  const day = localTimeComponent(timestamp, "day");
  return `${year}-${month}-${day}`;
};

export const dateForDateTimeInputValue = (timestamp) => {
  const year = localTimeComponent(timestamp, "year");
  const month = localTimeComponent(timestamp, "month");
  const day = localTimeComponent(timestamp, "day");
  const hour = localTimeComponent(timestamp, "hour");
  const minute = localTimeComponent(timestamp, "minute");
  return `${year}-${month}-${day}T${hour}:${minute}`;
};

export const cookieValue = (key) =>
  (new RegExp(`${key || "="}=(.*?); `, "gm").exec(`${document.cookie}; `) || [
    "",
    null,
  ])[1];

export const removeSpinner = (selector) => {
  $(selector).prop("disabled", false);
  selector.split(",").map((s) => $(`${s} .sw-spinner`).remove());
};

export const addSpinner = (selector, timeout) => {
  removeSpinner(selector); // ensure no other spinners are present

  $(selector).append(
    '<i class="ml-2 fa-solid fa-circle-notch fa-spin fast-spin sw-spinner"></i>',
  );
  $(selector).prop("disabled", true);

  if (timeout) {
    setTimeout(() => {
      removeSpinner(selector);
    }, timeout);
  }
};

export const isUrl = (text) => {
  const expr =
    /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)?$/gi;
  return !!text.match(new RegExp(expr));
};

export const regularizedPhone = (text) => {
  const ret = text.replace(/[^\d]/g, "");
  const n = Math.max(ret.length - 10, 0);
  return ret.slice(n);
};

export const toPercent = (num, den, cap = 100, limit = 20) => {
  if (!den) return "-";
  num = Math.min(num, den);
  let ret = (num / den) * 100;
  if (num > limit && ret > cap) ret = cap - (num % 4);
  return `${Math.ceil(ret)}%`;
};

const getDate = (selector, dateType) => {
  const { startDate, endDate } = $(`${selector}`).data("daterangepicker");
  const date = dateType === "start" ? startDate : endDate;
  return moment(date).local().format("YYYY-MM-DD");
};

export const getStartDate = (selector) => getDate(selector, "start");

export const getEndDate = (selector) => getDate(selector, "end");

export const handleToggleFilter = ({
  items,
  ids,
  toggleSelector,
  tableId,
  fillTable,
  table,
  property,
}) => {
  let filteredItems = [];
  const toggle = $(toggleSelector).val();
  switch (toggle) {
    case "OR":
      filteredItems = items.filter((item) =>
        item[property]?.some((id) => ids.includes(id)),
      );
      break;
    case "AND":
      filteredItems = items.filter((item) =>
        ids.every((id) => item[property]?.includes(id)),
      );
      break;
    case "NOT":
      filteredItems = items.filter(
        (item) =>
          item[property]?.length &&
          !item[property].some((id) => ids.includes(id)),
      );
      break;
    default:
  }

  if (ids.length === 0) filteredItems = items;

  if (tableId && fillTable) fillTable(tableId, filteredItems);
  else if (table) {
    table.clear();
    table.rows.add(filteredItems).draw();
  }
};

export const addSearchToggleClickEvent = ({
  drawTable = false,
  callback = null,
  includeNot = true,
}) => {
  $(document)
    .off("click", ".boolean-search-toggle")
    .on("click", ".boolean-search-toggle", (e) => {
      const el = $(e.currentTarget);

      const toggleType = el.hasClass("comparison-toggle")
        ? "comparison"
        : "boolean";

      const hiddenSelectpicker = el
        .parents(".boolean-selector")
        .find(".hidden-selectpicker");

      const currentValue = hiddenSelectpicker.val();
      let newValue;

      if (toggleType === "comparison")
        newValue = currentValue === ">" ? "<" : ">";
      else if (includeNot)
        switch (currentValue) {
          case "OR":
            newValue = "AND";
            break;
          case "AND":
            newValue = "NOT";
            break;
          case "NOT":
            newValue = "OR";
            break;
          default:
        }
      else newValue = currentValue === "OR" ? "AND" : "OR";

      hiddenSelectpicker.val(newValue);

      if (callback) callback();

      el.find(".option").hide();
      el.find(`.option[data-value="${newValue}"]`).show();

      if (drawTable) $(`#${drawTable}`).DataTable().draw();
    });
};

export const capitalize = (word) =>
  word.charAt(0).toUpperCase() + word.slice(1);

export const timestampToDuration = (timestamp) => {
  if (timestamp == null) return "00:00";

  const pad = (n) => String(n).padStart(2, "0");

  const h = Math.floor(timestamp / 3600);
  const m = Math.floor((timestamp % 3600) / 60);
  const s = parseInt(timestamp % 60, 10);

  return h ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
};

export const EU_DIALLER_COUNTRIES = ["AT", "CZ", "DE", "FI", "NL", "PT", "RO"];
export const userDiallerCountries = () => {
  const regions = getUser().diallerRegions || [];
  return regions.includes("EU")
    ? [...EU_DIALLER_COUNTRIES, ...regions.filter((r) => r !== "EU")]
    : regions;
};

const addSpinnerHandlers = (opts, newOpts) => {
  if (opts.spinner) {
    addSpinner(opts.spinner);
    const { complete } = newOpts;
    newOpts.complete = () => {
      removeSpinner(opts.spinner);
      if (complete) complete();
    };
  }
};

const handle400 = (text, opts) => {
  if (opts.userMustConfirmError) messageAlert(text);
  else {
    const { title = "An error occurred", type = "danger" } = opts.enrichToast
      ? opts.enrichToast(text) || {}
      : {};

    showToast({
      title,
      subtitle: text,
      type,
    });
  }
};

export const wrappedGet = (opts) => {
  const sentryError = new Error("wrappedGet");
  const newOpts = { ...opts };
  newOpts.error = (err, textStatus) => {
    if (err.status === 401) {
      localStorage.removeItem("provider");
      localStorage.removeItem("clarity");
      const { pathname } = window.location;
      const { search } = window.location;
      if (
        !SIGNIN_PAGES.includes(pathname) &&
        !opts.url.includes("/internal-api")
      )
        window.location.replace(`/signin?redirect=${pathname}${search}`);
    } else {
      if ([400, 402].includes(err.status)) handle400(err.responseJSON, opts);
      if (opts.error) opts.error(err);
    }
    if (![0, 400, 401, 402, 502, 504].includes(err.status))
      console.error(opts.url, err.status);
    if (err.status === 502) {
      const error = new Error(`GET ${opts.url}: ${textStatus}`, {
        cause: sentryError,
      });
      error.response = err.responseText;
      error.status = err.status;
      Sentry.captureException(error);
    }
  };
  addSpinnerHandlers(opts, newOpts);
  $.get(newOpts);
};

const wrappedPut = (opts) => {
  const sentryError = new Error("wrappedPut");
  const newOpts = { ...opts };
  newOpts.error = (err, textStatus) => {
    showToast({
      title: "Unable to proceed",
      subtitle: "Please try again or contact support if the issue persists",
      type: "danger",
    });
    if (opts.error) opts.error(err, textStatus);
    console.error(opts.url, err.status);
    if (err.status === 502 && sentryError) {
      const error = new Error(`PUT ${opts.url}: ${textStatus}`, {
        cause: sentryError,
      });
      error.response = err.responseText;
      error.status = err.status;
      Sentry.captureException(error);
    }
  };
  newOpts.contentType = "";
  newOpts.type = "PUT";

  addSpinnerHandlers(opts, newOpts);
  $.ajax(newOpts);
};

const wrappedPostInner = (opts, sentryError) => {
  const newOpts = { ...opts };

  if (newOpts.contentType === undefined)
    newOpts.contentType = "application/json";

  newOpts.error = (err, textStatus) => {
    if ([400, 402].includes(err.status)) {
      handle400(err.responseJSON || err.responseText, opts);
    } else if (err.status === 451) {
      showToast({
        title: "Restricted Mode",
        subtitle: `The following path was blocked: ${opts.url}`,
        type: "warning",
      });
    } else if (err.status !== 401) {
      showToast({
        title: "Unable to proceed",
        subtitle: "Please try again or contact support if the issue persists",
        type: "danger",
      });
    }
    if (opts.error) opts.error(err);
    if (![0, 400, 401, 402, 451, 502, 504].includes(err.status)) {
      console.error(opts.url, err.status);
    }
    if (err.status === 502 && sentryError) {
      const error = new Error(`POST ${opts.url}: ${textStatus}`, {
        cause: sentryError,
      });
      error.response = err.responseText;
      error.status = err.status;
      Sentry.captureException(error);
    }
  };
  addSpinnerHandlers(opts, newOpts);
  $.post(newOpts);
};

export const largePOST = (opts, sentryError = null) => {
  sentryError = sentryError || new Error("largePOST");
  wrappedGet({
    url: "/api/requests/get_upload_url",
    success: (res) => {
      const url = `/upload/${res.url.split("/upload/")[1]}`;
      wrappedPut({
        url,
        data: opts.data,
        success: () => {
          const newOpts = { ...opts };
          newOpts.data = JSON.stringify({ url, endpoint: opts.url });
          newOpts.url = "/api/requests/call_endpoint";
          wrappedPostInner(newOpts, sentryError);
        },
        error: opts.error,
      });
    },
    error: opts.error,
  });
};

export const wrappedPost = (opts) => {
  const sentryError = new Error("wrappedPOST");
  if (opts.data && opts.data.length > 6000000) {
    if (opts.url.startsWith("/")) {
      largePOST(opts, sentryError);
      return;
    }
  }
  const newOpts = { ...opts };
  newOpts.error = (err) => {
    // API Gateway sometimes returns 500 when request is too large
    if ([413, 500].includes(err.status)) {
      if (opts.url.startsWith("/")) {
        largePOST(opts, sentryError);
        return;
      }
    }
    if (opts.error) opts.error(err);
  };
  wrappedPostInner(newOpts, sentryError);
};

export const createDurationInterval = (id) => {
  const start = Date.now() / 1000;

  return setInterval(() => {
    const diff = Date.now() / 1000 - start;
    $(`#${id}`).text(timestampToDuration(diff));
  }, 1000);
};

export const initializeGeneralUtils = () => {
  $(".alert").on("close.bs.alert", (e) => {
    e.preventDefault();
    $(".alert").hide();
  });

  $(".datepicker").each((_, e) => {
    if ($(e).hasClass("multi") || $(e).hasClass("time")) return;
    if (!$(e).find("input").length) return;
    if ($(e).hasClass("internal")) return;
    $(e)
      .find("input")
      .daterangepicker({
        autoApply: true,
        singleDatePicker: true,
        startDate: new Date(),
        minDate: $(e).hasClass("any-date") ? "" : new Date(),
        opens: $(e).hasClass("left") ? "left" : "right",
        locale: { format: "DD MMM YYYY" },
      });
  });

  $(".datepicker.time").each((_, e) => {
    $(e)
      .find("input")
      .daterangepicker({
        parentEl: `#${$(e).data("parent")}`,
        autoApply: true,
        singleDatePicker: true,
        startDate: moment().add(10, "minutes"),
        minDate: $(e).hasClass("any-date") ? "" : new Date(),
        opens: $(e).hasClass("left") ? "left" : "right",
        drops: $(e).hasClass("up") ? "up" : "down",
        locale: { format: "YYYY-MM-DDTHH:mm" },
        timePicker: true,
        timePicker24Hour: true,
      });
  });

  $(".datepicker.multi").each((_, e) => {
    if ($(e).hasClass("internal")) return;
    const { teamCreatedAt } = getUser();
    const direction = $(e).hasClass("left") ? "left" : "right";
    const drops = $(e).hasClass("up") ? "up" : "down";
    let startDate;
    if ($(e).hasClass("sw-all-time")) {
      startDate = new Date("07/01/2021");
    } else if ($(e).hasClass("all-time")) {
      startDate = new Date("01/01/2020");
    } else {
      startDate = moment().subtract(29, "days");
    }

    $(e)
      .find("input")
      .daterangepicker(
        {
          autoApply: true,
          ranges: {
            Today: [moment(), moment()],
            Yesterday: [
              moment().subtract(1, "days"),
              moment().subtract(1, "days"),
            ],
            "Last 7 Days": [moment().subtract(6, "days"), moment()],
            "Last 30 Days": [moment().subtract(29, "days"), moment()],
            "Last 90 Days": [moment().subtract(89, "days"), moment()],
            "This Week": [moment().startOf("week"), moment().endOf("week")],
            "Last Week": [
              moment().subtract(1, "week").startOf("week"),
              moment().subtract(1, "week").endOf("week"),
            ],
            "This Month": [moment().startOf("month"), moment().endOf("month")],
            "Last Month": [
              moment().subtract(1, "month").startOf("month"),
              moment().subtract(1, "month").endOf("month"),
            ],
            "This Quarter": [
              moment().quarter(moment().quarter()).startOf("quarter"),
              moment().quarter(moment().quarter()).endOf("quarter"),
            ],
            "Last Quarter": [
              moment()
                .quarter(moment().quarter())
                .subtract(1, "Q")
                .startOf("quarter"),
              moment()
                .quarter(moment().quarter())
                .subtract(1, "Q")
                .endOf("quarter"),
            ],
            "This Year": [moment().startOf("year"), moment()],
            "Last Year": [
              moment().subtract(1, "years").startOf("year"),
              moment().subtract(1, "years").endOf("year"),
            ],
            "All Time": [
              $(e).hasClass("all-time")
                ? new Date("01/01/2020")
                : new Date("07/01/2021"),
              moment(),
            ],
          },
          showCustomRangeLabel: false,
          alwaysShowCalendars: true,
          linkedCalendars: false,
          startDate,
          endDate: new Date(),
          minDate: teamCreatedAt
            ? moment.unix(teamCreatedAt).format("DD MMM YYYY")
            : new Date("01/01/2020"),
          maxDate: new Date(),
          opens: direction,
          drops,
          locale: {
            format: "DD MMM YYYY",
          },
        },
        () => {
          setDateRangePickerChanged(true);
        },
      );
  });

  $(document).on(
    "click",
    ".dropdown-menu .dropdown-item.contains-submenu",
    (e) => {
      e.stopImmediatePropagation();
      $(e.currentTarget).find(".dropdown-menu.submenu").toggle();
      $(".dropdown").on("hide.bs.dropdown", () =>
        $(e.currentTarget).find(".submenu").hide(),
      );
    },
  );

  $("#backBtn").click(() => {
    window.history.back();
  });

  $(".disabled-allow-pointer-events").on("click", (e) => {
    e.preventDefault();
  });

  $(".alphaonly").on(
    "keydown",
    (event) =>
      event.key.match(/^[a-zA-Z\s]+$/) ||
      ["Backspace", "Tab", "ArrowLeft", "ArrowRight"].includes(event.key),
  );
};

export const capitalizeFirstLetter = (string) =>
  string ? string.charAt(0).toUpperCase() + string.slice(1) : "";
